/**
 * GitlabConnectModal — credential entry form for self-hosted GitLab.
 *
 * Shared between:
 *   - IntegrationsPage (the canonical Settings → Integrations page)
 *   - MarketplaceDetailModal (Connect button on a marketplace card whose
 *     workflow declares `gitlab` as a required integration — opening this
 *     modal in-place beats sending the user away to /integrations and
 *     back).
 *
 * Why a component and not just JSX duplicated in two places:
 *   - One source of truth for the form layout + validation + error states
 *   - Both callers want IDENTICAL submit behavior (mutation, toast,
 *     error rendering) — extracting also dedupes the imperative logic
 *
 * The hook (useConnectGitlab) owns the network call + cache invalidation
 * so this component stays presentation-focused. Reset state on isOpen
 * transitions so reopening always starts from a clean form.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectGitlab } from '../../hooks/useIntegrations';
// Reuses the existing .gitlab-*-modal CSS shipped with IntegrationsPage —
// importing here so the styles travel with the component wherever it's
// rendered, instead of relying on the host page having loaded the sheet.
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function GitlabConnectModal({ isOpen, onClose, onConnected }) {
  const [instanceUrl, setInstanceUrl] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectGitlab();

  // Reset the form whenever the modal transitions to open. Avoids leaking
  // a partially-typed value across reopens.
  useEffect(() => {
    if (isOpen) {
      setInstanceUrl('');
      setAccessToken('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!instanceUrl.trim() || !accessToken.trim()) {
      setError('Both fields are required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      { instanceUrl: instanceUrl.trim(), accessToken: accessToken.trim() },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.();
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect GitLab. Please check your URL and token.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="gitlab-connect-modal"
    >
      {/* Stop click propagation at the modal-content level. React
          events propagate through portals — without this, a click on
          a form field or button would bubble up through the React
          tree, reach the marketplace modal's backdrop click handler,
          and close BOTH modals (the marketplace one unmounts, taking
          this one with it via the React parent-child relationship). */}
      <div
        className="gitlab-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect GitLab</h2>
        <p className="gitlab-modal-description">
          Enter your self-hosted GitLab instance URL and a Personal Access Token with <code>read_api</code> and <code>read_repository</code> scopes.
        </p>

        <div className="gitlab-form-group">
          <label className="gitlab-form-label">GitLab Instance URL</label>
          <input
            type="text"
            value={instanceUrl}
            placeholder="https://gitlab.company.com"
            onChange={(e) => { setInstanceUrl(e.target.value || ''); setError(''); }}
            className="gitlab-form-input"
            disabled={connecting}
          />
        </div>

        <div className="gitlab-form-group">
          <label className="gitlab-form-label">Personal Access Token</label>
          <input
            type="password"
            value={accessToken}
            placeholder="glpat-xxxxxxxxxxxxxxxxxxxx"
            onChange={(e) => { setAccessToken(e.target.value || ''); setError(''); }}
            className="gitlab-form-input"
            disabled={connecting}
          />
        </div>

        {error && <p className="gitlab-modal-error">{error}</p>}

        <div className="gitlab-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !instanceUrl.trim() || !accessToken.trim()}
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
